/**
 * Returns a debounced function that waits for the given delay before running.
 * Resets the timer if called again within the delay.
 *
 * @template A - Type of the argument passed to the function.
 * @template R - Return type of the wrapped function.
 * @param func - The function to debounce.
 * @param wait - The number of milliseconds to delay invocation (default is 250 ms).
 * @returns A debounced version of the given function, also exposes a `.cancel()` method.
 */
export default function debounce<A, R>(
    func: (arg: A) => R,
    wait = 250
): ((arg: A) => void) & { cancel: () => void } {
    let timeout: ReturnType<typeof setTimeout>;

    const debounced = (arg: A) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(arg), wait);
    };

    debounced.cancel = () => {
        clearTimeout(timeout);
    };

    return debounced;
}
